import { useMemo } from 'react'
import { useUserProfile } from '@/presentation/hooks/useUserProfile'
import { useHomeRevenueSummary } from './useHomeRevenueSummary'
import {
  EMPTY_DATA_PLACEHOLDER_COUNT,
  formatRevenueDisplay,
  getPreviousMonthName,
  mapRevenueToChartItems,
} from './useHomeViewModel.logic'
import { useLast7DaysEarnings } from './useLast7DaysEarnings'

export function useHomeViewModel() {
  // --- Data Queries ---
  const { userProfile, isUserProfileLoading } = useUserProfile()
  const revenueQuery = useHomeRevenueSummary()
  const chartQuery = useLast7DaysEarnings()

  // --- Computed UI State ---
  const monthName = useMemo(() => getPreviousMonthName(), [])

  const revenueSummary = useMemo(() => {
    const monthlyAmount = revenueQuery.data?.monthly?.monthly_revenue
    const lastMonthAmount = revenueQuery.data?.lastMonth?.last_month_revenue

    return {
      monthlyAmount: revenueQuery.isLoading
        ? 'Loading...'
        : formatRevenueDisplay(monthlyAmount),
      lastMonthCard: {
        display: revenueQuery.isLoading
          ? 'Loading...'
          : formatRevenueDisplay(lastMonthAmount),
        shouldShow: !!(lastMonthAmount && Number(lastMonthAmount) > 0),
        monthName,
        isLoading: revenueQuery.isLoading,
        isError: revenueQuery.isError,
      },
    }
  }, [
    revenueQuery.data,
    revenueQuery.isLoading,
    revenueQuery.isError,
    monthName,
  ])

  const weeklyChart = useMemo(() => {
    const rawData = chartQuery.data || []
    const amounts = rawData.map((d) => Number(d.revenue))
    const maxAmount = Math.max(...amounts, 0)

    const shouldShowEmptyState =
      chartQuery.isError ||
      (rawData.length === 0 && !chartQuery.isLoading && !chartQuery.isFetching)

    const displayData = shouldShowEmptyState
      ? mapRevenueToChartItems(
          new Array(EMPTY_DATA_PLACEHOLDER_COUNT).fill({}),
          0,
          true,
        )
      : mapRevenueToChartItems(rawData, maxAmount)

    return {
      isLoading: chartQuery.isLoading,
      isFetching: chartQuery.isFetching,
      isError: chartQuery.isError,
      displayData,
      shouldShowEmptyState,
      onReload: () => chartQuery.refetch(),
    }
  }, [
    chartQuery.data,
    chartQuery.isLoading,
    chartQuery.isFetching,
    chartQuery.isError,
  ])

  return {
    greeting: {
      nickname: isUserProfileLoading
        ? 'Loading...'
        : userProfile?.nickname || '<Unknown>',
      isLoading: false, // 逻辑已经合并到 nickname 中，保持接口兼容
    },
    revenueSummary,
    weeklyChart,
  }
}
